import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { State, Action, StateContext } from '@ngxs/store';
import { environment } from '../../environments/environment';
import { lastValueFrom, map } from 'rxjs';
import { LanguageService } from '../services/language.service';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';


export class LoadHeaderData {
  static readonly type = '[GlobalData] Load Header';
  constructor(public language?: string) {}
}

export class LoadFooterData {
  static readonly type = '[GlobalData] Load Footer';
  constructor(public language?: string) {}
}

export class LoadLanguages {
  static readonly type = '[GlobalData] Load Languages';
}

export interface GlobalDataStateModel {
  headerData: any;
  footerData: any;
  languages: any[];
}

@State<GlobalDataStateModel>({
  name: 'globalData',
  defaults: {
    headerData: null,
    footerData: null,
    languages: []
  }
})
@Injectable()
export class ApplicationState {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.strapiUrl.replace(/\/+$/, '');
  private readonly languageService = inject(LanguageService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  @Action(LoadHeaderData)
  async loadHeaderData(
    ctx: StateContext<GlobalDataStateModel>,
    action: LoadHeaderData
  ) {
    try {
      const language = action.language ?? this.getLanguageFromUrl() ?? 'nl';
      const res = await lastValueFrom<any>(this.http.get(`${this.apiUrl}/global?locale=${language}`));

      ctx.patchState({ headerData: res?.data?.header });
    } catch (err) {
      console.error('Error fetching header data', err);
    }
  }

  @Action(LoadFooterData)
  async loadFooterData(
    ctx: StateContext<GlobalDataStateModel>,
    action: LoadFooterData
  ) {
    try {
      const language = action.language ?? this.getLanguageFromUrl() ?? 'nl';
      const res = await lastValueFrom<any>(this.http.get(`${this.apiUrl}/global?locale=${language}`));

      ctx.patchState({ footerData: res?.data?.footer });
    } catch (err) {
      console.error('Error fetching footer data', err);
    }
  }

  @Action(LoadLanguages)
  async loadLanguages(
    ctx: StateContext<GlobalDataStateModel>,
    action: LoadLanguages
  ) {
    try {
      const res = await lastValueFrom<any>(this.http.get(`${this.apiUrl}/i18n/locales`));

      ctx.patchState({ languages: res });
    } catch (err) {
      console.error('Error fetching footer data', err);
    }
  }

  private getLanguageFromUrl(): string {
    if (isPlatformBrowser(this.platformId)) {
      const segments = window.location.pathname.split('/');
      return segments[1] || 'nl'; // assumes '/:language/...'
    }
    return 'nl';
  }
}
